import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, PlayCircle, Award, MessageCircle, Shield, Clock, FileText, Star, Loader2, Instagram, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG = "https://customer-assets-cm19k8pv.emergentagent.net/job_nail-academy-es/artifacts/dpza9d6o_IMG_6245.webp";
const VALERIIA_IMG = "https://customer-assets-cm19k8pv.emergentagent.net/job_nail-academy-es/artifacts/56gor8ft_IMG_2974.JPG";
const NAIL_1 = "https://images.unsplash.com/photo-1604654894610-df63bc536371?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";
const NAIL_2 = "https://images.unsplash.com/photo-1610992015762-45dca7fa3a85?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";
const BOOK_IMG = "https://customer-assets-cm19k8pv.emergentagent.net/job_nail-academy-es/artifacts/6x9u5kr9_image.png";

const TIERS = [
  {
    key: "basic_course",
    name: "Básico",
    price: 50,
    regular: 120,
    total: 60.5,
    highlight: false,
    features: [
      "Acceso completo a todos los módulos en vídeo HD",
      "Libro digital PDF descargable con teoría",
      "Conceptos y apuntes por lección",
      "Acceso ilimitado a la plataforma",
      "Certificado digital de finalización",
    ],
  },
  {
    key: "premium_course",
    name: "Premium VIP",
    price: 100,
    regular: 200,
    total: 121.0,
    highlight: true,
    features: [
      "Todo lo incluido en el plan Básico",
      "Soporte personal de Valeriia durante 3 meses",
      "Respuesta a todas tus dudas técnicas",
      "Revisión de trabajos y correcciones",
      "Asesoramiento para lanzar tu negocio",
    ],
  },
];

const FAQ = [
  { q: "¿Cuándo tendré acceso al curso?", a: "Inmediatamente tras el pago. Recibirás un email con tu usuario y contraseña para acceder al aula online." },
  { q: "¿Cuánto dura el acceso?", a: "El acceso es de por vida. Podrás repasar los módulos siempre que quieras desde cualquier dispositivo." },
  { q: "¿El precio incluye IVA?", a: "Los precios mostrados son sin IVA. Se añade el 21% de IVA en el checkout según la normativa española (DGT V0778-26)." },
  { q: "¿Puedo pedir factura?", a: "Sí. Al finalizar la compra podrás introducir tus datos fiscales y recibirás la factura por email." },
  { q: "¿En qué se diferencia el plan Premium?", a: "Además de todo el material del plan Básico, tendrás soporte personal directo de Valeriia durante 3 meses para resolver todas tus dudas." },
  { q: "¿Puedo devolverlo si no me gusta?", a: "Al tratarse de contenido digital de acceso inmediato, según el art. 103.m del RDLeg 1/2007, aceptas la renuncia expresa al derecho de desistimiento al iniciar la reproducción del contenido." },
];

export default function Landing() {
  const [preview, setPreview] = useState({ modules: [] });
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/course/preview").then((r) => setPreview(r.data)).catch(() => {});
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const handleBuy = async (tier) => {
    setBuying(tier.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar el pago");
      setBuying(null);
    }
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[var(--bg-main)]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[var(--border-subtle)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="text-[var(--brand-primary)]" size={22} />
            <span className="font-heading text-xl">Valeriia <em className="text-[var(--brand-primary)] not-italic">Nail Academy</em></span>
          </div>
          <div className="hidden md:flex gap-6 text-sm">
            <button data-testid="nav-curriculo" onClick={() => scrollTo("curriculo")} className="hover:text-[var(--brand-primary)]">Currículo</button>
            <button data-testid="nav-instructora" onClick={() => scrollTo("instructora")} className="hover:text-[var(--brand-primary)]">Instructora</button>
            <button data-testid="nav-precios" onClick={() => scrollTo("precios")} className="hover:text-[var(--brand-primary)]">Precios</button>
            <button data-testid="nav-faq" onClick={() => scrollTo("faq")} className="hover:text-[var(--brand-primary)]">FAQ</button>
          </div>
          <Link data-testid="nav-login" to="/login" className="btn-outline-brand !py-2 !px-5 text-sm">Acceder</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="rose-grain">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-white border border-[var(--border-subtle)] text-xs uppercase tracking-widest text-[var(--brand-primary)] mb-6">
              Curso online · Español · Acceso inmediato
            </span>
            <h1 className="font-heading text-5xl md:text-6xl leading-[1.05] mb-6">
              Domina la <em className="text-[var(--brand-primary)] not-italic">manicura profesional</em> con Valeriia
            </h1>
            <p className="text-lg text-[var(--text-muted)] mb-8 max-w-xl">
              Aprende manicura con gel, esmaltado semipermanente y técnica profesional paso a paso. Un curso pensado para el mercado español con todo lo que necesitas para trabajar como profesional desde el primer día.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <button data-testid="hero-cta-precios" onClick={() => scrollTo("precios")} className="btn-brand">
                Ver tarifas <Sparkles size={18} />
              </button>
              <button data-testid="hero-cta-curriculo" onClick={() => scrollTo("curriculo")} className="btn-outline-brand">
                <PlayCircle size={18} /> Ver contenido
              </button>
              <a data-testid="hero-instagram" href="https://www.instagram.com/valeriia_beauty_space?stkn=aGxtdzhuZXd4Z2Nj&utm_source=qr" target="_blank" rel="noreferrer" aria-label="Instagram"
                 className="w-12 h-12 rounded-full border-[1.5px] border-[var(--brand-primary)] text-[var(--brand-primary)] flex items-center justify-center hover:bg-[var(--brand-primary-light)] transition-colors">
                <Instagram size={20}/>
              </a>
              <a data-testid="hero-whatsapp" href="https://wa.me/message/AYCIE24LFTYEK1" target="_blank" rel="noreferrer" aria-label="WhatsApp"
                 className="w-12 h-12 rounded-full border-[1.5px] border-[var(--brand-primary)] text-[var(--brand-primary)] flex items-center justify-center hover:bg-[var(--brand-primary-light)] transition-colors">
                <MessageCircle size={20}/>
              </a>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-[var(--text-muted)]">
              <div className="flex items-center gap-2"><Shield size={16} className="text-[var(--brand-primary)]"/>Pago seguro Stripe</div>
              <div className="flex items-center gap-2"><Award size={16} className="text-[var(--brand-primary)]"/>Certificado incluido</div>
            </div>
          </div>
          <div className="relative">
            <img src={HERO_IMG} alt="Manicura profesional" className="rounded-2xl shadow-2xl w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-white border border-[var(--border-subtle)] rounded-2xl p-4 shadow-xl flex items-center gap-3">
              <div className="flex -space-x-2">
                {[NAIL_1, NAIL_2, VALERIIA_IMG].map((s,i)=>(<img key={i} src={s} className="w-10 h-10 rounded-full object-cover border-2 border-white"/>))}
              </div>
              <div>
                <div className="flex gap-0.5 text-[var(--brand-gold)]"><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/><Star size={14} fill="currentColor"/></div>
                <p className="text-xs text-[var(--text-muted)]">+100 alumnas formadas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: PlayCircle, title: "12 lecciones en vídeo HD", desc: "Contenido estructurado en 3 módulos progresivos que puedes ver a tu ritmo." },
            { icon: FileText, title: "Manual PDF + apuntes", desc: "Libro digital descargable y conceptos por lección para consultar cuando quieras." },
            { icon: MessageCircle, title: "Soporte de Valeriia (Premium)", desc: "3 meses de resolución de dudas personalizada para acelerar tu progreso." },
          ].map((b, i) => (
            <div key={i} className="card-luxe p-8">
              <b.icon className="text-[var(--brand-primary)] mb-4" size={28} />
              <h3 className="text-xl mb-2">{b.title}</h3>
              <p className="text-[var(--text-muted)] text-sm">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Instructora */}
      <section id="instructora" className="bg-[var(--bg-alt)]">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-5 gap-12 items-center">
          <img src={VALERIIA_IMG} alt="Valeriia" className="md:col-span-2 rounded-2xl aspect-square object-cover shadow-xl" />
          <div className="md:col-span-3">
            <span className="text-xs uppercase tracking-widest text-[var(--brand-primary)]">Tu instructora</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-2 mb-6">Hola, soy <em className="text-[var(--brand-primary)] not-italic">Valeriia</em></h2>
            <p className="text-lg text-[var(--text-muted)] mb-4">
              Con más de 5 años de experiencia en manicura con gel y formada profesionalmente, he acompañado a más de 100 alumnas a convertirse en profesionales en España.
            </p>
            <p className="text-lg text-[var(--text-muted)]">
              En este curso comparto absolutamente todo lo que sé: desde la técnica hasta cómo darte de alta como autónoma, fijar precios y captar clientas fieles.
            </p>
          </div>
        </div>
      </section>

      {/* Nails Book */}
      <section id="libro" className="max-w-6xl mx-auto px-6 py-20">
        <div className="card-luxe overflow-hidden grid md:grid-cols-2">
          <div className="bg-[var(--bg-alt)] flex items-center justify-center p-8">
            <img src={BOOK_IMG} alt="Nails Book — libro de manicura" className="rounded-xl shadow-xl w-full max-w-xs object-cover" />
          </div>
          <div className="p-8 md:p-12">
            <span className="text-xs uppercase tracking-widest text-[var(--brand-primary)]">Empieza por aquí · Solo 15€</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-2 mb-4">Nails Book <em className="text-[var(--brand-primary)] not-italic">en PDF</em></h2>
            <p className="text-[var(--text-muted)] mb-6">La guía teórica completa del curso en formato libro digital. Descarga inmediata tras el pago y acceso para siempre.</p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0"/>
                <span><strong>¿No sabes si la manicura es para ti?</strong> Descúbrelo sin invertir en el curso completo</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0"/>
                <span><strong>¿Ya trabajas como manicurista?</strong> Perfecciona tu técnica con teoría profesional estructurada</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0"/>
                <span>Estructura de la uña, enfermedades, desinfección, materiales y fundamentos de la técnica</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0"/>
                <span>Acceso inmediato: tras el pago recibes tus credenciales por email</span>
              </li>
            </ul>
            <div className="flex items-end gap-2 mb-4">
              <span className="font-heading text-4xl text-[var(--brand-primary)]">15€</span>
              <span className="text-xs text-[var(--text-muted)] mb-1.5">+ IVA 21% · Total 18,15 €</span>
            </div>
            <button
              data-testid="buy-nails_book-btn"
              onClick={() => handleBuy({ key: "nails_book" })}
              disabled={buying === "nails_book"}
              className="btn-brand"
            >
              {buying === "nails_book" ? (<><Loader2 className="animate-spin" size={18}/> Redirigiendo a Stripe…</>) : "Comprar Nails Book"}
            </button>
            <p className="text-xs text-[var(--text-muted)] mt-4 flex items-center gap-1.5">
              <Sparkles size={13} className="text-[var(--brand-gold)]"/> Si luego compras el curso completo, el Nails Book ya está incluido.
            </p>
          </div>
        </div>
      </section>

      {/* Currículo */}
      <section id="curriculo" className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-widest text-[var(--brand-primary)]">Programa completo</span>
          <h2 className="font-heading text-4xl md:text-5xl mt-2">Qué vas a aprender</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {preview.modules.map((m) => (
            <div key={m.id} data-testid={`module-${m.id}`} className="card-luxe p-6">
              <div className="flex items-start justify-between mb-3">
                <span className="font-heading text-3xl text-[var(--brand-primary)]">{String(m.order).padStart(2, "0")}</span>
                <span className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Clock size={12}/>{m.lessons.length} lecciones</span>
              </div>
              <h3 className="text-xl mb-2">{m.title}</h3>
              <p className="text-sm text-[var(--text-muted)] mb-4">{m.description}</p>
              <ul className="space-y-1.5">
                {m.lessons.map((l) => (
                  <li key={l.id} className="text-sm flex items-center gap-2">
                    <PlayCircle size={14} className="text-[var(--brand-primary)] flex-shrink-0"/>
                    <span className="text-[var(--text-dark)] flex-1">{l.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Precios */}
      <section id="precios" className="bg-[var(--bg-alt)]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-widest text-[var(--brand-primary)]">Elige tu plan</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-2">Precios de lanzamiento</h2>
            <p className="text-[var(--text-muted)] mt-3">Descuento por tiempo limitado. Pago único, acceso de por vida.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {TIERS.map((t) => (
              <div key={t.key} data-testid={`pricing-${t.key}`} className={`card-luxe p-8 relative ${t.highlight ? "ring-2 ring-[var(--brand-primary)]" : ""}`}>
                {t.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--brand-primary)] text-white text-xs px-4 py-1 rounded-full uppercase tracking-widest">Más popular</span>
                )}
                <h3 className="font-heading text-3xl mb-2">{t.name}</h3>
                <div className="flex items-end gap-2 mb-1">
                  <span className="font-heading text-5xl text-[var(--brand-primary)]">{t.price}€</span>
                  <span className="text-[var(--text-muted)] line-through mb-2">{t.regular}€</span>
                </div>
                <p className="text-xs text-[var(--text-muted)] mb-6">+ IVA 21% · Total {t.total.toFixed(2)} €</p>
                <ul className="space-y-3 mb-8">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0"/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`buy-${t.key}-btn`}
                  onClick={() => handleBuy(t)}
                  disabled={buying === t.key}
                  className={t.highlight ? "btn-brand w-full justify-center" : "btn-outline-brand w-full justify-center"}
                >
                  {buying === t.key ? (<><Loader2 className="animate-spin" size={18}/> Redirigiendo a Stripe…</>) : `Comprar ${t.name}`}
                </button>
                <p className="text-[11px] text-[var(--text-muted)] mt-3 text-center leading-snug">
                  Al comprar aceptas los <Link to="/legal/terminos" className="underline">Términos</Link> y la <Link to="/legal/privacidad" className="underline">Privacidad</Link>. El pago lo procesa Stripe de forma segura.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest text-[var(--brand-primary)]">Dudas frecuentes</span>
          <h2 className="font-heading text-4xl md:text-5xl mt-2">Preguntas frecuentes</h2>
        </div>
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-[var(--border-subtle)]">
              <AccordionTrigger className="text-left font-heading text-lg">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[var(--text-muted)]">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--brand-secondary)] text-white/80">
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="text-[var(--brand-rose)]" size={20}/>
              <span className="font-heading text-white text-xl">Valeriia Nail Academy</span>
            </div>
            <p className="text-sm">Formación online profesional de manicura desde España.</p>
          </div>
          <div>
            <h4 className="text-white mb-3 font-heading">Legal (España)</h4>
            <ul className="space-y-2 text-sm">
              <li><Link data-testid="footer-aviso-legal" to="/legal/aviso-legal" className="hover:text-white">Aviso Legal</Link></li>
              <li><Link data-testid="footer-privacidad" to="/legal/privacidad" className="hover:text-white">Política de Privacidad (RGPD)</Link></li>
              <li><Link data-testid="footer-cookies" to="/legal/cookies" className="hover:text-white">Política de Cookies</Link></li>
              <li><Link data-testid="footer-terminos" to="/legal/terminos" className="hover:text-white">Términos y Condiciones</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white mb-3 font-heading">Contacto</h4>
            <div className="flex flex-col gap-2.5">
              <a data-testid="footer-instagram" href="https://www.instagram.com/valeriia_beauty_space?stkn=aGxtdzhuZXd4Z2Nj&utm_source=qr" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm hover:text-white transition-colors">
                <Instagram size={16} className="text-[var(--brand-rose)]"/> Instagram
              </a>
              <a data-testid="footer-whatsapp" href="https://wa.me/message/AYCIE24LFTYEK1" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm hover:text-white transition-colors">
                <MessageCircle size={16} className="text-[var(--brand-rose)]"/> WhatsApp
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs">
          © {new Date().getFullYear()} Valeriia Nail Academy · Todos los derechos reservados
        </div>
      </footer>

    </div>
  );
}
